import {
  ACCRUALS_CONFIG_TABS,
  createEmptyCondition,
} from './accrualsConfigurationConstants';
import {
  countEstimationRules,
  loadEstimationRulesFromStorage,
  serializeEstimationRulesForStorage,
} from './accrualsEstimationRuleUtils';

const STORAGE_PREFIX = 'accruals-configuration-rules';

function emptyRulesState() {
  return {
    [ACCRUALS_CONFIG_TABS.ESTIMATION]: [],
    [ACCRUALS_CONFIG_TABS.INFO_REQUEST]: [],
  };
}

export function getRulesStorageKey(projectId) {
  return `${STORAGE_PREFIX}-${projectId}`;
}

export function loadRulesByTab(projectId) {
  if (!projectId) return emptyRulesState();
  try {
    const raw = window.localStorage.getItem(getRulesStorageKey(projectId));
    if (!raw) return emptyRulesState();
    const parsed = JSON.parse(raw);
    return {
      ...emptyRulesState(),
      ...parsed,
    };
  } catch (err) {
    return emptyRulesState();
  }
}

/** Estimation rules only — Info Request conditions always come from the IM rule API. */
export function loadEstimationRulesByTab(projectId) {
  const loaded = loadRulesByTab(projectId);
  const estimationRules = loadEstimationRulesFromStorage(loaded[ACCRUALS_CONFIG_TABS.ESTIMATION] || []);
  return {
    ...emptyRulesState(),
    [ACCRUALS_CONFIG_TABS.ESTIMATION]: estimationRules,
    [ACCRUALS_CONFIG_TABS.INFO_REQUEST]: [],
    meta: loaded.meta || {},
  };
}

export function loadRulesMetaByTab(projectId) {
  const loaded = loadRulesByTab(projectId);
  return loaded.meta || {};
}

export function saveRulesByTab(projectId, rulesByTab) {
  if (!projectId) return;
  window.localStorage.setItem(
    getRulesStorageKey(projectId),
    JSON.stringify(rulesByTab),
  );
}

export function saveEstimationRulesByTab(projectId, estimationRules, metaByTab = {}) {
  const existing = loadRulesByTab(projectId);
  saveRulesByTab(projectId, {
    [ACCRUALS_CONFIG_TABS.ESTIMATION]: serializeEstimationRulesForStorage(estimationRules || []),
    [ACCRUALS_CONFIG_TABS.INFO_REQUEST]: [],
    meta: {
      ...(existing.meta || {}),
      ...metaByTab,
    },
  });
}

export function countConfiguredRules(rulesByTab = {}) {
  return Object.values(rulesByTab).reduce(
    (total, rules) => total + (Array.isArray(rules) ? rules.length : 0),
    0,
  );
}

/** Count configured estimation rules. */
export function countEstimationRulesConfigured(rulesByTab = {}) {
  return countEstimationRules(rulesByTab?.[ACCRUALS_CONFIG_TABS.ESTIMATION] || []);
}

/** Info Request has a single DB rule (groups or flat conditions) — count as 0 or 1. */
export function isInfoRequestRuleConfigured(imRule) {
  if (!imRule || imRule.status === 'disabled') return false;
  const version = Number(imRule.version) || 0;
  if (version <= 0) return false;
  const groups = imRule.groups || [];
  if (Array.isArray(groups) && groups.some((g) => (g.conditions || []).length > 0)) {
    return true;
  }
  const conditions = imRule.conditions || [];
  return Array.isArray(conditions) && conditions.length > 0;
}

export function countInfoRequestRulesConfigured(imRule) {
  return isInfoRequestRuleConfigured(imRule) ? 1 : 0;
}

export function countEstimationConditions(rulesByTab = {}) {
  const conditions = rulesByTab?.[ACCRUALS_CONFIG_TABS.ESTIMATION] || [];
  return Array.isArray(conditions) ? conditions.length : 0;
}

/** Active conditions only — disabled rules retain history in API but count as empty in UI. */
export function getActiveInfoRequestConditions(imRule) {
  if (!isInfoRequestRuleConfigured(imRule)) return [];
  return Array.isArray(imRule?.conditions) ? imRule.conditions : [];
}

export function countInfoRequestConditions(imRule) {
  return getActiveInfoRequestConditions(imRule).length;
}

export function createInitialBuilderConditions() {
  return [createEmptyCondition(0)];
}
